import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "./ThemeContext";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const UsernameSelection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { roomId, roomName, isCreator, gameMode, timeHorizon } = state;
  const [username, setUsername] = useState("");
  const [userId, setUserId] = useState("");
  const [error, setError] = useState(null);
  const [usernames, setUsernames] = useState([]);
  const [websocket, setWebSocket] = useState(null);
  const [hasSetUsername, setHasSetUsername] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const { theme, isAscii: isAnyAscii } = useTheme();
  const isDark = theme === "dark";
  const isAscii = isAnyAscii && isDark;
  const isAsciiLight = isAnyAscii && !isDark;
  const s = isAscii ? asciiStyles : isAsciiLight ? asciiLightStyles : isDark ? darkStyles : lightStyles;

  useEffect(() => {
    const ws = new WebSocket(`wss://${process.env.REACT_APP_BACKEND_URL_NO_PORT}/ws/${roomName}`);

    ws.onopen = () => {
      console.log("WebSocket connection opened");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Received WebSocket message:", data);

      if (data.usernames) {
        setUsernames(data.usernames);
      }

      if (data.gameStarted) {
        setGameStarted(true);
        if (userId) {
          navigate('/game', {
            state: {
              username,
              roomId,
              roomName,
              isMultiplayer: true,
              usernames: data.usernames,
              userId,
              gameMode: data.gameMode || gameMode || 'finite',
              timeHorizon: data.timeHorizon ?? timeHorizon ?? 10,
            }
          });
        } else {
          setError("User ID not yet available. Please try again.");
        }
      }
    };

    setWebSocket(ws);

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [roomName, userId, username, roomId, navigate]);

  const handleUsernameSubmit = async () => {
    if (!username) {
      setError("Please provide a username.");
      return;
    }

    try {
      const response = await axios.post(`${backendUrl}/set-username/`, {
        room_id: roomId,
        username
      });

      const newUserId = response.data.id;
      console.log('Set username response:', response);
      console.log('User ID:', newUserId);

      setUserId(newUserId);

      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(`username:${username}`);
        setHasSetUsername(true);
        setError("");
      } else {
        setError("Connection error. Please refresh the page.");
      }
    } catch (err) {
      console.error("Error setting username:", err);
      setError(err.response?.data?.detail || "Error setting username. Please try again.");
    }
  };

  const handleStartGame = async () => {
    if (!userId) {
      setError("Please wait for user ID to be set before starting the game.");
      return;
    }

    try {
      await axios.post(`${backendUrl}/start-game/`, { room_id: parseInt(roomId) });

      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({ type: 'start_game', gameMode: gameMode || 'finite', timeHorizon: timeHorizon ?? 10 }));
      } else {
        setError("Connection error. Please refresh the page.");
      }
    } catch (error) {
      console.error("Error starting game:", error);
      setError("Failed to start the game. Please try again.");
    }
  };

  const getFloatingNameStyle = (index) => ({
    position: "absolute",
    top: `${Math.random() * 80 + 10}%`,
    left: `${Math.random() * 80 + 10}%`,
    transform: `translate(-50%, -50%)`,
    animation: `float ${3 + index % 3}s ease-in-out infinite`,
    backgroundColor: isAscii ? "#000000" : isAsciiLight ? "#e8e8e4" : isDark ? "rgba(0, 180, 120, 0.3)" : "#66bb6a",
    color: isAscii ? "#33ff33" : isAsciiLight ? "#1a1a1a" : isDark ? "#00f5c4" : "white",
    border: isAscii ? "1px solid #33ff33" : isAsciiLight ? "1px solid #444" : isDark ? "1px solid rgba(0, 245, 196, 0.4)" : "none",
    boxShadow: isAscii ? "0 0 8px rgba(51,255,51,0.2)" : isAsciiLight ? "none" : isDark ? "0 0 10px rgba(0, 245, 196, 0.2)" : "0 2px 4px rgba(0,0,0,0.1)",
    padding: "8px 15px",
    borderRadius: "20px",
    fontSize: "1rem",
    fontWeight: "bold",
    zIndex: 1,
  });

  const renderContent = () => {
    if (gameStarted) {
      return <h2 style={s.gameStarting}>Game Starting...</h2>;
    }

    if (!hasSetUsername) {
      return (
        <div style={s.inputContainer}>
          <input
            type="text"
            placeholder="Enter Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={s.input}
          />
          {error && <p style={s.error}>{error}</p>}
          <button onClick={handleUsernameSubmit} style={s.submitButton}>
            Set Username
          </button>
        </div>
      );
    }

    if (isCreator) {
      return (
        <div style={s.creatorContainer}>
          <h3 style={s.hostMessage}>You are the room host</h3>
          <button
            onClick={handleStartGame}
            style={{
              ...s.startButton,
              backgroundColor: isAscii
                ? (usernames.length < 2 ? "#000" : "#33ff33")
                : isAsciiLight
                ? (usernames.length < 2 ? "#ccc" : "#1a1a1a")
                : isDark
                ? (usernames.length < 2 ? "rgba(60,60,80,0.5)" : "rgba(0,100,200,0.5)")
                : (usernames.length < 2 ? "#cccccc" : "#1976d2"),
              border: isAscii
                ? `1px solid ${usernames.length < 2 ? "rgba(51,255,51,0.3)" : "#33ff33"}`
                : isAsciiLight
                ? `1px solid ${usernames.length < 2 ? "#aaa" : "#444"}`
                : isDark
                ? `1px solid ${usernames.length < 2 ? "rgba(100,100,140,0.3)" : "rgba(0,180,255,0.4)"}`
                : "none",
              color: isAscii
                ? (usernames.length < 2 ? "rgba(51,255,51,0.4)" : "#000")
                : isAsciiLight
                ? (usernames.length < 2 ? "#888" : "#f2f2ee")
                : isDark
                ? (usernames.length < 2 ? "#556" : "#00d4ff")
                : "white",
              boxShadow: isAscii && usernames.length >= 2
                ? "0 0 12px rgba(51,255,51,0.3)"
                : isDark && usernames.length >= 2 ? "0 0 14px rgba(0,180,255,0.25)" : "none",
              cursor: usernames.length < 2 ? "not-allowed" : "pointer",
              fontFamily: (isAscii || isAsciiLight) ? '"Courier New", Courier, monospace' : undefined,
              borderRadius: (isAscii || isAsciiLight) ? "0" : undefined,
            }}
            disabled={usernames.length < 2}
          >
            Start Game
          </button>
          {usernames.length < 2 && (
            <p style={s.waitingMessage}>Waiting for more players to join...</p>
          )}
        </div>
      );
    }

    return (
      <p style={s.waitingMessage}>
        Waiting for the host to start the game...
      </p>
    );
  };

  return (
    <div style={s.container}>
      <h1 style={s.header}>Welcome to Room: {roomName}</h1>
      {renderContent()}
      <div style={s.floatingContainer}>
        <h3 style={s.playersHeader}>
          Players in Room: <span style={s.playerCount}>({usernames.length})</span>
        </h3>
        {usernames.map((name, index) => (
          <div key={index} style={getFloatingNameStyle(index)}>
            {name}
          </div>
        ))}
      </div>
    </div>
  );
};

const lightStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#f0fff4",
    padding: "20px",
  },
  header: {
    fontSize: "2rem",
    marginBottom: "20px",
    color: "#2e7d32",
    textAlign: "center",
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  input: {
    fontSize: "1rem",
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    width: "300px",
    maxWidth: "90%",
  },
  submitButton: {
    fontSize: "1rem",
    padding: "10px 20px",
    borderRadius: "5px",
    border: "none",
    backgroundColor: "#2e7d32",
    color: "white",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  startButton: {
    fontSize: "1.2rem",
    padding: "12px 24px",
    borderRadius: "5px",
    border: "none",
    color: "white",
    transition: "all 0.3s",
    marginTop: "20px",
  },
  error: {
    color: "#e53935",
    margin: "10px 0",
  },
  creatorContainer: {
    textAlign: "center",
    margin: "20px 0",
  },
  hostMessage: {
    color: "#1976d2",
    fontSize: "1.4rem",
    margin: "10px 0",
  },
  waitingMessage: {
    color: "#666",
    fontSize: "1.2rem",
    margin: "20px 0",
    textAlign: "center",
  },
  gameStarting: {
    color: "#2e7d32",
    fontSize: "1.8rem",
    textAlign: "center",
    margin: "20px 0",
  },
  floatingContainer: {
    position: "relative",
    width: "100%",
    height: "300px",
    overflow: "hidden",
    marginTop: "20px",
    textAlign: "center",
  },
  playersHeader: {
    color: "#2e7d32",
    marginBottom: "15px",
  },
  playerCount: {
    fontSize: "0.9em",
    color: "#666",
  },
};

const darkStyles = {
  ...lightStyles,
  container: {
    ...lightStyles.container,
    background: "transparent",
  },
  header: {
    ...lightStyles.header,
    color: "#00f5c4",
    textShadow: "0 0 20px rgba(0, 245, 196, 0.3)",
  },
  input: {
    ...lightStyles.input,
    background: "rgba(4, 7, 18, 0.8)",
    border: "1px solid rgba(0, 245, 255, 0.2)",
    color: "#ddeeff",
  },
  submitButton: {
    ...lightStyles.submitButton,
    backgroundColor: "transparent",
    border: "1px solid rgba(0, 245, 196, 0.5)",
    color: "#00f5c4",
    boxShadow: "0 0 12px rgba(0, 245, 196, 0.2)",
  },
  error: {
    ...lightStyles.error,
    color: "#ff6b9d",
  },
  hostMessage: {
    ...lightStyles.hostMessage,
    color: "#00d4ff",
    textShadow: "0 0 12px rgba(0, 212, 255, 0.3)",
  },
  waitingMessage: {
    ...lightStyles.waitingMessage,
    color: "#8ab4cc",
  },
  gameStarting: {
    ...lightStyles.gameStarting,
    color: "#00f5c4",
    textShadow: "0 0 20px rgba(0, 245, 196, 0.4)",
  },
  playersHeader: {
    ...lightStyles.playersHeader,
    color: "#00f5c4",
  },
  playerCount: {
    ...lightStyles.playerCount,
    color: "#8ab4cc",
  },
};

const asciiLightStyles = {
  ...lightStyles,
  container: { ...lightStyles.container, background: "#e8e8e4" },
  header: { ...lightStyles.header, color: "#1a1a1a", fontFamily: '"Courier New", Courier, monospace' },
  input: { ...lightStyles.input, background: "#f8f8f6", border: "1px solid #444", color: "#1a1a1a", borderRadius: "0", fontFamily: '"Courier New", Courier, monospace' },
  submitButton: { ...lightStyles.submitButton, backgroundColor: "#1a1a1a", border: "1px solid #444", color: "#f2f2ee", borderRadius: "0", fontFamily: '"Courier New", Courier, monospace' },
  hostMessage: { ...lightStyles.hostMessage, color: "#1a1a1a", fontFamily: '"Courier New", Courier, monospace' },
  waitingMessage: { ...lightStyles.waitingMessage, color: "#555", fontFamily: '"Courier New", Courier, monospace' },
  gameStarting: { ...lightStyles.gameStarting, color: "#1a1a1a", fontFamily: '"Courier New", Courier, monospace' },
  playersHeader: { ...lightStyles.playersHeader, color: "#1a1a1a", fontFamily: '"Courier New", Courier, monospace' },
  playerCount: { ...lightStyles.playerCount, color: "#555" },
};

const asciiStyles = {
  ...darkStyles,
  container: { ...lightStyles.container, background: "#000000" },
  header: {
    ...lightStyles.header,
    color: "#33ff33",
    textShadow: "0 0 12px rgba(51,255,51,0.5)",
    fontFamily: '"Courier New", Courier, monospace',
  },
  input: {
    ...lightStyles.input,
    background: "#000000",
    border: "1px solid #33ff33",
    color: "#33ff33",
    borderRadius: "0",
    fontFamily: '"Courier New", Courier, monospace',
  },
  submitButton: {
    ...lightStyles.submitButton,
    backgroundColor: "#000000",
    border: "1px solid #33ff33",
    color: "#33ff33",
    borderRadius: "0",
    boxShadow: "0 0 10px rgba(51,255,51,0.2)",
    fontFamily: '"Courier New", Courier, monospace',
  },
  error: { ...lightStyles.error, color: "#ff6b9d" },
  hostMessage: {
    ...lightStyles.hostMessage,
    color: "#33ff33",
    textShadow: "0 0 10px rgba(51,255,51,0.4)",
    fontFamily: '"Courier New", Courier, monospace',
  },
  waitingMessage: {
    ...lightStyles.waitingMessage,
    color: "rgba(51,255,51,0.6)",
    fontFamily: '"Courier New", Courier, monospace',
  },
  gameStarting: {
    ...lightStyles.gameStarting,
    color: "#33ff33",
    textShadow: "0 0 18px rgba(51,255,51,0.5)",
    fontFamily: '"Courier New", Courier, monospace',
  },
  playersHeader: {
    ...lightStyles.playersHeader,
    color: "#33ff33",
    fontFamily: '"Courier New", Courier, monospace',
  },
  playerCount: {
    ...lightStyles.playerCount,
    color: "rgba(51,255,51,0.6)",
  },
};

// Add keyframes for floating animation
const floatStyle = document.createElement("style");
floatStyle.innerHTML = `
  @keyframes float {
    0% { transform: translate(-50%, -50%) translateY(0); }
    50% { transform: translate(-50%, -50%) translateY(-20px); }
    100% { transform: translate(-50%, -50%) translateY(0); }
  }
`;
document.head.appendChild(floatStyle);

export default UsernameSelection;
